/**
 * Contract Amendment History
 *
 * Displays the amendment audit trail for a specific contract.
 * Used within the ContractCard details section.
 */

import { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { ArrowUpCircle, ArrowDownCircle, DollarSign, History, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface Amendment {
  id: string;
  amendment_type: string;
  old_total_leads: number;
  new_total_leads: number;
  old_price_per_lead: number;
  new_price_per_lead: number;
  leads_delta: number;
  leads_delivered_at_time: number;
  amount_cents: number;
  notes: string | null;
  created_at: string;
}

interface ContractAmendmentHistoryProps {
  contractId: string;
}

const TYPE_LABELS: Record<string, string> = {
  upgrade: 'Upgrade',
  downgrade: 'Downgrade',
  price_change: 'Price Change',
  upgrade_and_price_change: 'Upgrade + Price',
  downgrade_and_price_change: 'Downgrade + Price',
};

export function ContractAmendmentHistory({ contractId }: ContractAmendmentHistoryProps) {
  const [amendments, setAmendments] = useState<Amendment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetch() {
      setLoading(true);
      const { data, error } = await supabase
        .from('contract_amendments')
        .select('id, amendment_type, old_total_leads, new_total_leads, old_price_per_lead, new_price_per_lead, leads_delta, leads_delivered_at_time, amount_cents, notes, created_at')
        .eq('contract_id', contractId)
        .order('created_at', { ascending: false })
        .limit(20);

      if (!cancelled) {
        if (error) {
          console.error('[AmendmentHistory] Error:', error);
        }
        setAmendments(data ?? []);
        setLoading(false);
      }
    }

    fetch();
    return () => { cancelled = true; };
  }, [contractId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
        <Loader2 className="h-3 w-3 animate-spin" />
        Loading amendments...
      </div>
    );
  }

  if (amendments.length === 0) {
    return (
      <p className="text-xs text-muted-foreground py-1">No amendments</p>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <History className="h-3 w-3" />
        Amendment History ({amendments.length})
      </div>
      {amendments.map((a) => {
        const isUpgrade = a.leads_delta > 0;
        const isDowngrade = a.leads_delta < 0;
        const leadsChanged = a.leads_delta !== 0;
        const priceChanged = a.old_price_per_lead !== a.new_price_per_lead;

        return (
          <div key={a.id} className="rounded border bg-muted/20 p-2 space-y-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                {isUpgrade && <ArrowUpCircle className="h-3 w-3 text-green-600" />}
                {isDowngrade && <ArrowDownCircle className="h-3 w-3 text-orange-600" />}
                {!leadsChanged && <DollarSign className="h-3 w-3 text-blue-600" />}
                <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                  {TYPE_LABELS[a.amendment_type] ?? a.amendment_type}
                </Badge>
              </div>
              <span className="text-[10px] text-muted-foreground">
                {new Date(a.created_at).toLocaleDateString('en-US', {
                  month: 'short', day: 'numeric', year: 'numeric',
                  hour: '2-digit', minute: '2-digit',
                })}
              </span>
            </div>
            <div className="text-xs space-y-0.5">
              {leadsChanged && (
                <p>Leads: {a.old_total_leads} → {a.new_total_leads} ({isUpgrade ? '+' : ''}{a.leads_delta})</p>
              )}
              {priceChanged && (
                <p>Price: ${a.old_price_per_lead.toFixed(2)} → ${a.new_price_per_lead.toFixed(2)}</p>
              )}
              {a.amount_cents !== 0 && (
                <p className={a.amount_cents > 0 ? 'text-green-700' : 'text-orange-700'}>
                  {a.amount_cents > 0
                    ? `Charge: $${(a.amount_cents / 100).toFixed(2)}`
                    : `Credit: -$${(Math.abs(a.amount_cents) / 100).toFixed(2)}`
                  }
                </p>
              )}
              {a.notes && <p className="italic text-muted-foreground">{a.notes}</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
